/**
 * RTWO Native Currency Engine — Monetary Policy Service
 */

import type { CurrencyId, PolicyId, RTWOMonetaryPolicy } from '../domain-models';
import type { IMonetaryPolicyService, MonetaryPolicyResult } from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class MonetaryPolicyService extends BaseNATVService implements IMonetaryPolicyService {
  readonly serviceId = 'natv-monetary-policy-001';
  readonly serviceName = 'Monetary Policy Service';
  readonly description =
    'Monetary policy service — placeholder, requires Genesis Configuration and Governance Runtime. No fake monetary policy values.';
  readonly integrations = ['Core Engine', 'Ledger Engine', 'Governance (future)', 'Event Bus'];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = true;

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async configureMonetaryPolicy(
    currencyId: CurrencyId,
    supplyModel: string,
    issuancePolicy: string,
    burnPolicy: string,
    inflationPolicy: string,
    deflationPolicy: string
  ): Promise<MonetaryPolicyResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        policyId: null,
        error:
          'Monetary policy configuration requires Genesis Configuration — Monetary Policy Pending',
        isPlaceholder: true,
        source: 'Native Currency Engine — Monetary Policy Pending',
      };
    }

    const adapterResult = await this.ledgerRegistry
      .getActiveAdapter()
      .configureMonetaryPolicy(
        currencyId,
        supplyModel,
        issuancePolicy,
        burnPolicy,
        inflationPolicy,
        deflationPolicy
      );

    if (adapterResult?.policy) {
      this.repositories.monetaryPolicies.save(adapterResult.policy);
      this.context.eventBus.emit(NATVInternalEventTypes.MONETARY_POLICY_CONFIGURED, {
        policyId: adapterResult.policy.policyId,
        currencyId,
      });
    }

    return {
      success: false,
      policyId: null,
      error:
        'Monetary policy configuration requires Genesis Configuration — Monetary Policy Pending',
      isPlaceholder: true,
      source: 'Native Currency Engine — Monetary Policy Pending',
    };
  }

  getPolicyByCurrencyId(currencyId: CurrencyId): RTWOMonetaryPolicy | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.monetaryPolicies.findByCurrencyId(currencyId);
  }

  getPolicy(policyId: PolicyId): RTWOMonetaryPolicy | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.monetaryPolicies.findById(policyId);
  }

  getAllPolicies(): RTWOMonetaryPolicy[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.monetaryPolicies.findAll();
  }
}
